import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  AlertTitle,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  ArrowBack,
  LocationOn,
  Phone,
  Email,
  Language,
  Star,
  CheckCircle,
  Cancel,
  AccessTime,
  Pets,
  SmokingRooms,
  Accessibility,
  Group,
  Edit,
  Message,
  CalendarToday,
  Add,
  Delete,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import AlertItem from '../../Components/AlertItem';
import UserSearchModal from '../../Components/UserSearchModal';
import ConfirmationDialog from '../../Components/ConfirmationDialog';
import CountrySelector from '../../Components/CountrySelector';
import StateSelector from '../../Components/StateSelector';
import ChapterSelector from '../../Components/ChapterSelector';
import {
  getBedAndBreakfast,
  updateBedAndBreakfast,
  createBnBBooking,
  updateBnBBooking,
  deleteBnBBooking,
  createBnBPricing,
  updateBnBPricing,
  deleteBnBPricing,
  sendBnBMessage,
  createBnBContact,
  updateBnBContact,
  deleteBnBContact,
} from '../../endpoints/bedandbreakfast';
import {
  BnBBookingCreateRequest,
  BnBBookingUpdateRequest,
  BnBAvailabilityResponse,
  BedAndBreakfast,
  BnBUpdateRequest,
  BnBPrice,
  BnBPriceCreateRequest,
  BnBPriceUpdateRequest,
  BnBMessageRequest,
  BnBContact,
  BnBContactCreateRequest,
  BnBContactUpdateRequest,
} from '../../types/bedandbreakfast';
import dayjs from 'dayjs';

const BnBDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showContactModal, setShowContactModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showEditPricingModal, setShowEditPricingModal] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [showEditBookingModal, setShowEditBookingModal] = useState(false);
  const [showUserSearchModal, setShowUserSearchModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<string | null>(null);
  const [showBookingDeleteConfirm, setShowBookingDeleteConfirm] =
    useState(false);
  const [bookingToDelete, setBookingToDelete] = useState<string | null>(null);
  const [editingBooking, setEditingBooking] = useState<any>(null);
  const [showPricingDeleteConfirm, setShowPricingDeleteConfirm] =
    useState(false);
  const [pricingToDelete, setPricingToDelete] = useState<string | null>(null);
  const [showBookingUserSearchModal, setShowBookingUserSearchModal] =
    useState(false);
  const [selectedBookingUser, setSelectedBookingUser] = useState<any>(null);
  const [showEditBnBModal, setShowEditBnBModal] = useState(false);
  const [bnbForm, setBnBForm] = useState<Partial<BnBUpdateRequest>>({});
  const [amenitiesString, setAmenitiesString] = useState<string>('');
  const [accessibilityString, setAccessibilityString] = useState<string>('');
  const [selectedCountry, setSelectedCountry] = useState<any>(null);
  const [selectedState, setSelectedState] = useState<any>(null);
  const [selectedChapter, setSelectedChapter] = useState<any>(null);
  const [messageText, setMessageText] = useState('');
  const [messageCheckInDate, setMessageCheckInDate] = useState<string>('');
  const [messageCheckOutDate, setMessageCheckOutDate] = useState<string>('');
  const [messageAlert, setMessageAlert] = useState<{
    severity: 'success' | 'error' | 'warning' | 'info';
    description: string;
    title?: string;
  } | null>(null);
  const [availabilityStartDate, setAvailabilityStartDate] =
    useState<string>('');
  const [availabilityEndDate, setAvailabilityEndDate] = useState<string>('');
  const [availabilityResult, setAvailabilityResult] =
    useState<BnBAvailabilityResponse | null>(null);
  const [bookingForm, setBookingForm] = useState<
    Partial<BnBBookingCreateRequest> & { status?: string }
  >({
    check_in_date: '',
    check_out_date: '',
    user_id: null,
    is_blocked: false,
    notes: '',
    status: 'pending',
  });
  const [pricingForm, setPricingForm] = useState<
    Partial<BnBPriceCreateRequest>
  >({
    name: '',
    price: 0,
    start_date: '',
    end_date: '',
  });
  const [editingPrice, setEditingPrice] = useState<BnBPrice | null>(null);
  const [contactForm, setContactForm] = useState<
    Partial<BnBContactCreateRequest>
  >({
    user_id: '',
    is_primary: false,
    show_email: false,
    email: '',
    show_phone: false,
    phone: '',
  });
  const [editingContact, setEditingContact] = useState<BnBContact | null>(null);

  const {
    data: bnb,
    isLoading,
    error,
  } = useApiGet(['bed-and-breakfast', id], () => getBedAndBreakfast(id!), {
    enabled: !!id,
    retry: 1,
  });

  // Bookings data is included in the main BnB response, no separate API call needed

  // Pricing data is included in the main BnB response, no separate API call needed

  const getPriceRangeColor = (priceRange?: string) => {
    switch (priceRange) {
      case 'budget':
        return 'success';
      case 'mid-range':
        return 'info';
      case 'upscale':
        return 'warning';
      case 'luxury':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriceRangeLabel = (priceRange?: string) => {
    switch (priceRange) {
      case 'budget':
        return 'Budget ($)';
      case 'mid-range':
        return 'Mid-Range ($$)';
      case 'upscale':
        return 'Upscale ($$$)';
      case 'luxury':
        return 'Luxury ($$$$)';
      default:
        return 'Price not specified';
    }
  };

  const getFeeStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'overdue':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getFeeStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle />;
      case 'overdue':
        return <Cancel />;
      case 'pending':
        return <AccessTime />;
      default:
        return null;
    }
  };

  // Message sending function
  const { mutate: sendMessage, isPending: isSendingMessage } = useApiSend(
    (data: BnBMessageRequest) => sendBnBMessage(id!, data),
    () => {
      setShowContactModal(false);
      setMessageText('');
      setMessageCheckInDate('');
      setMessageCheckOutDate('');
      setMessageAlert({
        severity: 'success',
        description:
          'Your message has been sent successfully! The property owner will contact you soon.',
      });
    },
    (error: Error) => {
      console.error('Error sending message:', error);
      setMessageAlert({
        severity: 'error',
        description:
          'Failed to send message. Please try again or contact support if the problem persists.',
      });
    }
  );

  const handleSendMessage = () => {
    if (!messageText.trim() || !messageCheckInDate || !messageCheckOutDate)
      return;

    // Validate date format before sending
    const checkInParsed = dayjs(messageCheckInDate);
    const checkOutParsed = dayjs(messageCheckOutDate);

    if (!checkInParsed.isValid() || !checkOutParsed.isValid()) {
      setMessageAlert({
        severity: 'error',
        description: 'Please enter valid dates for check-in and check-out.',
      });
      return;
    }

    const messageData: BnBMessageRequest = {
      message: messageText,
      check_in_date: messageCheckInDate,
      check_out_date: messageCheckOutDate,
    };

    sendMessage(messageData);
  };

  const handleEdit = () => {
    setShowEditModal(true);
  };

  // Mutations
  const { mutate: createBooking, isPending: isCreatingBooking } = useApiSend(
    (data: BnBBookingCreateRequest) => createBnBBooking(id!, data),
    () => {
      setShowBookingModal(false);
      setSelectedBookingUser(null);
      setBookingForm({
        check_in_date: '',
        check_out_date: '',
        user_id: null,
        is_blocked: false,
        notes: '',
        status: 'pending',
      });
      // Refetch the main BnB data to get updated bookings
      window.location.reload(); // Simple refresh for now, could be optimized with refetch
    },
    (error: Error) => {
      console.error('Error creating booking:', error);
    }
  );

  const { mutate: updateBooking, isPending: isUpdatingBooking } = useApiSend(
    (data: BnBBookingUpdateRequest) =>
      updateBnBBooking(id!, editingBooking!.id, data),
    () => {
      setShowEditBookingModal(false);
      setEditingBooking(null);
      setBookingForm({
        check_in_date: '',
        check_out_date: '',
        user_id: null,
        is_blocked: false,
        notes: '',
        status: 'pending',
      });
      // Refetch the main BnB data to get updated bookings
      window.location.reload(); // Simple refresh for now, could be optimized with refetch
    },
    (error: Error) => {
      console.error('Error updating booking:', error);
    }
  );

  const { mutate: deleteBooking, isPending: isDeletingBooking } = useApiSend(
    (bookingId: string) => deleteBnBBooking(id!, bookingId),
    () => {
      setShowBookingDeleteConfirm(false);
      setBookingToDelete(null);
      // Refetch the main BnB data to get updated bookings
      window.location.reload(); // Simple refresh for now, could be optimized with refetch
    },
    (error: Error) => {
      console.error('Error deleting booking:', error);
    }
  );

  const { mutate: updateBnB, isPending: isUpdatingBnB } = useApiSend(
    (data: BnBUpdateRequest) => updateBedAndBreakfast(id!, data),
    () => {
      setShowEditBnBModal(false);
      setBnBForm({});
      // Refetch the main BnB data to get updated information
      window.location.reload(); // Simple refresh for now, could be optimized with refetch
    },
    (error: Error) => {
      console.error('Error updating BnB:', error);
    }
  );

  const handleCheckAvailability = () => {
    if (!availabilityStartDate || !availabilityEndDate || !bnb) return;

    // Validate date format before proceeding
    const startDateParsed = dayjs(availabilityStartDate);
    const endDateParsed = dayjs(availabilityEndDate);

    if (!startDateParsed.isValid() || !endDateParsed.isValid()) {
      setAvailabilityResult({
        available: false,
        message: 'Please enter valid dates for check-in and check-out.',
      });
      return;
    }

    const startDate = availabilityStartDate;
    const endDate = availabilityEndDate;

    // Check if there's any data to work with
    const hasBookings = bnb.bookings && bnb.bookings.length > 0;
    const hasPricing = bnb.pricing && bnb.pricing.length > 0;

    if (!hasBookings && !hasPricing) {
      setAvailabilityResult({ available: false });
      return;
    }

    // Check for booking conflicts
    let hasBookingConflict = false;
    if (hasBookings) {
      hasBookingConflict = checkForBookingConflicts(
        bnb.bookings,
        startDate,
        endDate
      );
    }

    // Get applicable pricing
    const applicablePricing = hasPricing
      ? getPricesForDateRange(bnb, startDate, endDate)
      : [];

    // Determine availability and message
    if (hasBookingConflict) {
      setAvailabilityResult({
        available: false,
        message:
          'Not available for the selected dates due to existing bookings.',
      });
    } else if (hasPricing && applicablePricing.length > 0) {
      setAvailabilityResult({
        available: true,
        message: 'Potentially available! Please contact for verification.',
        pricing: applicablePricing,
      });
    } else if (hasBookings) {
      setAvailabilityResult({
        available: true,
        message: 'Potentially available! Please contact for verification.',
      });
    } else {
      setAvailabilityResult({
        available: false,
        message: 'No pricing information available for the selected dates.',
      });
    }
  };

  const checkForBookingConflicts = (
    bookings: any[],
    startDate: string,
    endDate: string
  ) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    return bookings.some((booking) => {
      const bookingStart = new Date(booking.check_in_date);
      const bookingEnd = new Date(booking.check_out_date);

      // Check if the requested date range overlaps with any existing booking
      return start < bookingEnd && end > bookingStart;
    });
  };

  const getPricesForDateRange = (
    bnb: BedAndBreakfast,
    startDate: string,
    endDate: string
  ) => {
    if (!bnb.pricing) return [];

    const start = new Date(startDate);
    const end = new Date(endDate);

    return bnb.pricing.filter((price) => {
      const priceStart = new Date(price.start_date);
      const priceEnd = price.end_date
        ? new Date(price.end_date)
        : new Date('2099-12-31');

      // Check if price range overlaps with requested date range
      return priceStart <= end && priceEnd >= start;
    });
  };

  const handleCreateBooking = () => {
    if (!id || !bookingForm.check_in_date || !bookingForm.check_out_date)
      return;

    const bookingData: BnBBookingCreateRequest = {
      bnb_id: id,
      check_in_date: bookingForm.check_in_date,
      check_out_date: bookingForm.check_out_date,
      user_id: bookingForm.user_id,
      is_blocked: bookingForm.is_blocked || false,
      notes: bookingForm.notes || null,
      status: bookingForm.status as 'confirmed' | 'pending' | 'cancelled',
    };

    createBooking(bookingData);
  };

  const handleUpdateBooking = () => {
    if (
      !editingBooking ||
      !bookingForm.check_in_date ||
      !bookingForm.check_out_date
    )
      return;

    const bookingData: BnBBookingUpdateRequest = {
      check_in_date: bookingForm.check_in_date,
      check_out_date: bookingForm.check_out_date,
      user_id: bookingForm.user_id,
      is_blocked: bookingForm.is_blocked || false,
      notes: bookingForm.notes || null,
      status: bookingForm.status as 'confirmed' | 'pending' | 'cancelled',
    };

    updateBooking(bookingData);
  };

  const handleEditBooking = (booking: any) => {
    setEditingBooking(booking);
    setBookingForm({
      check_in_date: booking.check_in_date.split('T')[0],
      check_out_date: booking.check_out_date.split('T')[0],
      user_id: booking.user_id,
      is_blocked: booking.is_blocked,
      notes: booking.notes || '',
      status: booking.status,
    });
    setShowEditBookingModal(true);
  };

  const handleDeleteBooking = (bookingId: string) => {
    setBookingToDelete(bookingId);
    setShowBookingDeleteConfirm(true);
  };

  const handleConfirmBookingDelete = () => {
    if (bookingToDelete) {
      deleteBooking(bookingToDelete);
    }
  };

  const handleCancelBookingDelete = () => {
    setShowBookingDeleteConfirm(false);
    setBookingToDelete(null);
  };

  const handleBookingUserSelected = (user: any) => {
    setSelectedBookingUser(user);
    setShowBookingUserSearchModal(false);
    // Update the booking form with the selected user
    setBookingForm((prev) => ({
      ...prev,
      user_id: user.id,
    }));
  };

  // Pricing management functions
  const { mutate: createPricing, isPending: isCreatingPricing } = useApiSend(
    (data: BnBPriceCreateRequest) => createBnBPricing(id!, data),
    () => {
      setShowPricingModal(false);
      setPricingForm({ name: '', price: 0, start_date: '', end_date: '' });
      // Refetch the main BnB data to get updated pricing
      window.location.reload(); // Simple refresh for now, could be optimized with refetch
    },
    (error: Error) => {
      console.error('Error creating pricing:', error);
    }
  );

  const { mutate: updatePricing, isPending: isUpdatingPricing } = useApiSend(
    (data: BnBPriceUpdateRequest) =>
      updateBnBPricing(id!, editingPrice!.id, data),
    () => {
      setShowEditPricingModal(false);
      setEditingPrice(null);
      setPricingForm({ name: '', price: 0, start_date: '', end_date: '' });
      // Refetch the main BnB data to get updated pricing
      window.location.reload(); // Simple refresh for now, could be optimized with refetch
    },
    (error: Error) => {
      console.error('Error updating pricing:', error);
    }
  );

  const { mutate: deletePricing, isPending: isDeletingPricing } = useApiSend(
    (priceId: string) => deleteBnBPricing(id!, priceId),
    () => {
      setShowPricingDeleteConfirm(false);
      setPricingToDelete(null);
      // Refetch the main BnB data to get updated pricing
      window.location.reload(); // Simple refresh for now, could be optimized with refetch
    },
    (error: Error) => {
      console.error('Error deleting pricing:', error);
    }
  );

  const handleCreatePricing = () => {
    if (!pricingForm.name || !pricingForm.price || !pricingForm.start_date)
      return;

    const pricingData: BnBPriceCreateRequest = {
      name: pricingForm.name,
      price: pricingForm.price,
      start_date: pricingForm.start_date,
      end_date: pricingForm.end_date || '',
    };

    createPricing(pricingData);
  };

  const handleUpdatePricing = () => {
    if (
      !editingPrice ||
      !pricingForm.name ||
      !pricingForm.price ||
      !pricingForm.start_date
    )
      return;

    const pricingData: BnBPriceUpdateRequest = {
      name: pricingForm.name,
      price: pricingForm.price,
      start_date: pricingForm.start_date,
      end_date: pricingForm.end_date || '',
    };

    updatePricing(pricingData);
  };

  const handleEditPricing = (price: BnBPrice) => {
    setEditingPrice(price);
    setPricingForm({
      name: price.name,
      price: price.price,
      start_date: price.start_date.split('T')[0],
      end_date: price.end_date ? price.end_date.split('T')[0] : '',
    });
    setShowEditPricingModal(true);
  };

  const handleDeletePricing = (priceId: string) => {
    setPricingToDelete(priceId);
    setShowPricingDeleteConfirm(true);
  };

  const handleConfirmPricingDelete = () => {
    if (pricingToDelete) {
      deletePricing(pricingToDelete);
    }
  };

  const handleCancelPricingDelete = () => {
    setShowPricingDeleteConfirm(false);
    setPricingToDelete(null);
  };

  const handleEditBnB = () => {
    if (!bnb) return;

    setBnBForm({
      name: bnb.name,
      description: bnb.description,
      city: bnb.city,
      state_id: bnb.state_id,
      amenities: bnb.amenities || [],
      policies: bnb.policies,
      cancellation_policy: bnb.cancellation_policy,
      pet_policy: bnb.pet_policy,
      smoking_policy: bnb.smoking_policy,
      accessibility_features: bnb.accessibility_features || [],
      local_chapter_id: bnb.local_chapter_id,
      is_active: bnb.is_active,
    });

    // Initialize selectors with current values
    setSelectedCountry(
      bnb.country_id ? { id: bnb.country_id, name: bnb.country } : null
    );
    setSelectedState(
      bnb.state_id ? { id: bnb.state_id, name: bnb.state } : null
    );
    setSelectedChapter(
      bnb.local_chapter_id
        ? { id: bnb.local_chapter_id, search_name: bnb.local_chapter_name }
        : null
    );

    // Initialize string states for comma-separated fields
    setAmenitiesString(
      Array.isArray(bnb.amenities) ? bnb.amenities.join(', ') : ''
    );
    setAccessibilityString(
      Array.isArray(bnb.accessibility_features)
        ? bnb.accessibility_features.join(', ')
        : ''
    );

    setShowEditBnBModal(true);
  };

  const handleCountryChange = (country: any) => {
    setSelectedCountry(country);
    // Reset state when country changes
    setSelectedState(null);
    setBnBForm((prev) => ({ ...prev, state_id: undefined }));
  };

  const handleStateChange = (state: any) => {
    setSelectedState(state);
    setBnBForm((prev) => ({ ...prev, state_id: state?.id }));
  };

  const handleChapterChange = (chapter: any) => {
    setSelectedChapter(chapter);
    setBnBForm((prev) => ({ ...prev, local_chapter_id: chapter?.id }));
  };

  const handleUpdateBnB = () => {
    if (
      !bnbForm.name ||
      !bnbForm.city ||
      !selectedState?.id ||
      !selectedChapter?.id
    )
      return;

    const updateData: BnBUpdateRequest = {
      name: bnbForm.name,
      description: bnbForm.description,
      city: bnbForm.city,
      state_id: selectedState.id,
      amenities: amenitiesString
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item),
      policies: bnbForm.policies,
      cancellation_policy: bnbForm.cancellation_policy,
      pet_policy: bnbForm.pet_policy,
      smoking_policy: bnbForm.smoking_policy,
      accessibility_features: accessibilityString
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item),
      local_chapter_id: selectedChapter.id,
      is_active: bnbForm.is_active,
    };

    updateBnB(updateData);
  };

  // Contact management functions
  const { mutate: createContact, isPending: isCreatingContact } = useApiSend(
    (data: BnBContactCreateRequest) => createBnBContact(id!, data),
    () => {
      setShowAddContactModal(false);
      setContactForm({ user_id: '', is_primary: false });
      setSelectedUser(null);
      // Refetch the main BnB data to get updated contacts
      window.location.reload(); // Simple refresh for now, could be optimized with refetch
    },
    (error: Error) => {
      console.error('Error creating contact:', error);
    }
  );

  const { mutate: updateContact, isPending: isUpdatingContact } = useApiSend(
    (data: BnBContactUpdateRequest) =>
      updateBnBContact(id!, editingContact!.id, data),
    () => {
      setShowEditContactModal(false);
      setEditingContact(null);
      setSelectedUser(null);
      setContactForm({ user_id: '', is_primary: false });
      // Refetch the main BnB data to get updated contacts
      window.location.reload(); // Simple refresh for now, could be optimized with refetch
    },
    (error: Error) => {
      console.error('Error updating contact:', error);
    }
  );

  const { mutate: deleteContact, isPending: isDeletingContact } = useApiSend(
    (contactId: string) => deleteBnBContact(id!, contactId),
    () => {
      // Refetch the main BnB data to get updated contacts
      window.location.reload(); // Simple refresh for now, could be optimized with refetch
    },
    (error: Error) => {
      console.error('Error deleting contact:', error);
    }
  );

  const handleCreateContact = () => {
    if (!contactForm.user_id) return;

    const contactData: BnBContactCreateRequest = {
      user_id: contactForm.user_id,
      is_primary: contactForm.is_primary || false,
    };

    // Include email/phone fields if show_email/show_phone are checked
    if (contactForm.show_email) {
      contactData.show_email = true;
      if (contactForm.email) {
        contactData.email = contactForm.email;
      }
    }
    if (contactForm.show_phone) {
      contactData.show_phone = true;
      if (contactForm.phone) {
        contactData.phone = contactForm.phone;
      }
    }

    createContact(contactData);
  };

  const handleUpdateContact = () => {
    if (!editingContact) return;

    // If no new user selected, only update the primary status
    const contactData: BnBContactUpdateRequest = {
      is_primary: contactForm.is_primary || false,
    };

    // Only include user_id if a new user was selected
    if (contactForm.user_id) {
      contactData.user_id = contactForm.user_id;
    }

    // Include email/phone fields if show_email/show_phone are checked
    if (contactForm.show_email !== undefined) {
      contactData.show_email = contactForm.show_email;
      if (contactForm.show_email && contactForm.email) {
        contactData.email = contactForm.email;
      } else if (!contactForm.show_email) {
        contactData.email = undefined;
      }
    }
    if (contactForm.show_phone !== undefined) {
      contactData.show_phone = contactForm.show_phone;
      if (contactForm.show_phone && contactForm.phone) {
        contactData.phone = contactForm.phone;
      } else if (!contactForm.show_phone) {
        contactData.phone = undefined;
      }
    }

    updateContact(contactData);
  };

  const handleEditContact = (contact: BnBContact) => {
    setEditingContact(contact);
    // Pre-populate with current contact data (we'll need to get user info from somewhere)
    // For now, we'll set a placeholder and let them search for a new user
    setContactForm({
      user_id: '', // We don't have user_id in BnBContact, so start empty
      is_primary: contact.is_primary,
      show_email: !!contact.email,
      email: contact.email || '',
      show_phone: !!contact.phone,
      phone: contact.phone || '',
    });
    setSelectedUser(null); // Clear any previous selection
    setShowEditContactModal(true);
  };

  const handleDeleteContact = (contactId: string) => {
    setContactToDelete(contactId);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (contactToDelete) {
      deleteContact(contactToDelete);
      setShowDeleteConfirm(false);
      setContactToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setContactToDelete(null);
  };

  const handleUserSelected = (user: any) => {
    setSelectedUser(user);
    setShowUserSearchModal(false);

    if (editingContact) {
      // Editing existing contact - update the form with selected user
      setContactForm((prev) => ({
        ...prev,
        user_id: user.id,
      }));
      // Stay in the edit modal
    } else {
      // Adding new contact - pre-populate with selected user
      setContactForm({
        user_id: user.id,
        is_primary: false,
      });
      setShowAddContactModal(true);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 2 }}>
          <AlertItem
            severity="error"
            description={`Error loading bed and breakfast: ${error.message}`}
            onClose={() => {}}
            keepOpen={true}
          />
        </Box>
        <Button variant="contained" onClick={() => navigate('/bnb')}>
          Back to List
        </Button>
      </Box>
    );
  }

  if (!bnb) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Bed and breakfast not found
        </Alert>
        <Button variant="contained" onClick={() => navigate('/bnb')}>
          Back to List
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Message Alert */}
      {messageAlert && (
        <Box sx={{ mb: 3 }}>
          <AlertItem
            severity={messageAlert.severity}
            title={
              messageAlert.title ||
              (messageAlert.severity === 'success' ? 'Message Sent!' : 'Error')
            }
            description={messageAlert.description}
            onClose={() => setMessageAlert(null)}
          />
        </Box>
      )}

      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/bnb')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Box flexGrow={1}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h4" gutterBottom>
              {bnb.name}
            </Typography>
            {bnb.can_edit && (
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={handleEditBnB}
                sx={{ ml: 2 }}
              >
                Edit Property
              </Button>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Box display="flex" alignItems="center">
              <LocationOn color="action" sx={{ mr: 0.5 }} />
              <Typography variant="body1">
                {bnb.city}, {bnb.state}
              </Typography>
            </Box>
            {(bnb as any).is_featured && (
              <Chip label="Featured" color="primary" size="small" />
            )}
            {(bnb as any).price_range && (
              <Chip
                label={getPriceRangeLabel((bnb as any).price_range)}
                color={getPriceRangeColor((bnb as any).price_range) as any}
                size="small"
              />
            )}
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Description */}
          {bnb.description && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  About
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
                  {bnb.description}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Amenities */}
          {bnb.amenities && bnb.amenities.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Amenities
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {bnb.amenities.map((amenity, index) => (
                    <Chip key={index} label={amenity} variant="outlined" />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Policies */}
          {(bnb.policies ||
            bnb.cancellation_policy ||
            bnb.pet_policy ||
            bnb.smoking_policy) && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Policies
                </Typography>
                <List dense>
                  {bnb.policies && (
                    <ListItem>
                      <ListItemText
                        primary="General Policies"
                        secondary={bnb.policies}
                      />
                    </ListItem>
                  )}
                  {bnb.cancellation_policy && (
                    <ListItem>
                      <ListItemText
                        primary="Cancellation Policy"
                        secondary={bnb.cancellation_policy}
                      />
                    </ListItem>
                  )}
                  {bnb.pet_policy && (
                    <ListItem>
                      <ListItemIcon>
                        <Pets />
                      </ListItemIcon>
                      <ListItemText
                        primary="Pet Policy"
                        secondary={bnb.pet_policy}
                      />
                    </ListItem>
                  )}
                  {bnb.smoking_policy && (
                    <ListItem>
                      <ListItemIcon>
                        <SmokingRooms />
                      </ListItemIcon>
                      <ListItemText
                        primary="Smoking Policy"
                        secondary={bnb.smoking_policy}
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          )}

          {/* Accessibility Features */}
          {bnb.accessibility_features &&
            bnb.accessibility_features.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Accessibility Features
                  </Typography>
                  <List dense>
                    {bnb.accessibility_features.map((feature, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <Accessibility />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}
          {/* Management Cards - Only show if user has permission */}
          {bnb.can_edit && (
            <>
              {/* Property Contacts */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography variant="h6">Property Contacts</Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Add />}
                      onClick={() => setShowUserSearchModal(true)}
                      color="success"
                    >
                      Add Contact
                    </Button>
                  </Box>
                  {bnb.contacts && bnb.contacts.length > 0 ? (
                    <List dense>
                      {bnb.contacts.map((contact) => (
                        <ListItem key={contact.id}>
                          <ListItemText
                            primary={`${contact.first_name} ${contact.last_name}`}
                            secondary={
                              <Box>
                                {contact.is_primary && (
                                  <Chip
                                    label="Primary Contact"
                                    color="primary"
                                    size="small"
                                    sx={{ mt: 0.5, mr: 1 }}
                                  />
                                )}
                                {contact.email && (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={0.5}
                                    mt={0.5}
                                  >
                                    <Email fontSize="small" color="action" />
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {contact.email}
                                    </Typography>
                                  </Box>
                                )}
                                {contact.phone && (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={0.5}
                                    mt={0.5}
                                  >
                                    <Phone fontSize="small" color="action" />
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {contact.phone}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            }
                          />
                          <Box display="flex" gap={1}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditContact(contact)}
                              title="Edit Contact"
                              sx={{ color: '#0072CE' }}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteContact(contact.id)}
                              color="error"
                              disabled={isDeletingContact}
                              title="Delete Contact"
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: 'center', py: 2 }}
                    >
                      No contacts assigned
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {/* Annual Fees */}
              {bnb.fees && bnb.fees.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6">Annual Fees</Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Edit />}
                        onClick={handleEdit}
                      >
                        Edit
                      </Button>
                    </Box>
                    <List dense>
                      {bnb.fees.map((fee) => (
                        <ListItem key={fee.id}>
                          <ListItemIcon>
                            {getFeeStatusIcon(fee.status)}
                          </ListItemIcon>
                          <ListItemText
                            primary={`${fee.year} - $${fee.amount.toFixed(2)}`}
                            secondary={
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Due:{' '}
                                  {dayjs(fee.due_date).format('MM/DD/YYYY')}
                                </Typography>
                                {fee.paid_date && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Paid:{' '}
                                    {dayjs(fee.paid_date).format('MM/DD/YYYY')}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                          <Chip
                            label={fee.status}
                            color={getFeeStatusColor(fee.status) as any}
                            size="small"
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              )}

              {/* Bookings */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography variant="h6">Bookings</Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Add />}
                      onClick={() => setShowBookingModal(true)}
                      color="success"
                    >
                      Add Booking
                    </Button>
                  </Box>
                  {bnb.full_bookings && bnb.full_bookings.length > 0 ? (
                    <List dense>
                      {bnb.full_bookings.map((booking) => (
                        <ListItem key={booking.id}>
                          <ListItemText
                            primary={`${booking.guest_name || 'Blocked'} - ${dayjs(booking.check_in_date).format('MM/DD/YYYY')} to ${dayjs(booking.check_out_date).format('MM/DD/YYYY')}`}
                            secondary={
                              <Box>
                                {booking.guest_email && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {booking.guest_email}
                                  </Typography>
                                )}
                                {booking.guest_phone && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {booking.guest_phone}
                                  </Typography>
                                )}
                                {booking.notes && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {booking.notes}
                                  </Typography>
                                )}
                                {booking.is_blocked && (
                                  <Chip
                                    label="Blocked"
                                    color="warning"
                                    size="small"
                                    sx={{ mt: 0.5 }}
                                  />
                                )}
                              </Box>
                            }
                          />
                          <Box display="flex" gap={1} alignItems="center">
                            <Chip
                              label={booking.status}
                              color={
                                booking.status === 'confirmed'
                                  ? 'success'
                                  : booking.status === 'pending'
                                    ? 'warning'
                                    : 'error'
                              }
                              size="small"
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleEditBooking(booking)}
                              title="Edit Booking"
                              sx={{ color: '#0072CE' }}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteBooking(booking.id)}
                              color="error"
                              disabled={isDeletingBooking}
                              title="Delete Booking"
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      textAlign="center"
                      py={2}
                    >
                      No bookings found
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {/* Pricing Management */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography variant="h6">Pricing</Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Add />}
                      onClick={() => setShowPricingModal(true)}
                      color="success"
                    >
                      Add Pricing
                    </Button>
                  </Box>
                  {bnb.pricing && bnb.pricing.length > 0 ? (
                    <List dense>
                      {bnb.pricing.map((price) => (
                        <ListItem key={price.id}>
                          <ListItemText
                            primary={`${price.name} - $${price.price}/night`}
                            secondary={
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {dayjs(price.start_date).format(
                                    'MMM D, YYYY'
                                  )}{' '}
                                  -{' '}
                                  {price.end_date
                                    ? dayjs(price.end_date).format(
                                        'MMM D, YYYY'
                                      )
                                    : 'Ongoing'}
                                </Typography>
                              </Box>
                            }
                          />
                          <Box display="flex" gap={1} alignItems="center">
                            <IconButton
                              size="small"
                              onClick={() => handleEditPricing(price)}
                              title="Edit Pricing"
                              sx={{ color: '#0072CE' }}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeletePricing(price.id)}
                              color="error"
                              disabled={isDeletingPricing}
                              title="Delete Pricing"
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      textAlign="center"
                      py={2}
                    >
                      No pricing found
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Contact */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Contact
              </Typography>
              {/* Find primary contact or first contact with info */}
              {(() => {
                const primaryContact = bnb.contacts?.find(
                  (c) => c.is_primary && (c.email || c.phone)
                );
                const firstContactWithInfo = bnb.contacts?.find(
                  (c) => c.email || c.phone
                );
                const displayContact = primaryContact || firstContactWithInfo;
                const hasContactInfo =
                  displayContact &&
                  (displayContact.email || displayContact.phone);
                const hasAnyContactInfo = bnb.contacts?.some(
                  (c) => c.email || c.phone
                );

                return (
                  <>
                    {hasContactInfo ? (
                      <List dense>
                        {displayContact.email && (
                          <ListItem>
                            <ListItemIcon>
                              <Email />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Button
                                  href={`mailto:${displayContact.email}`}
                                  size="small"
                                  sx={{
                                    textTransform: 'none',
                                    p: 0,
                                    minWidth: 'auto',
                                  }}
                                >
                                  {displayContact.email}
                                </Button>
                              }
                            />
                          </ListItem>
                        )}
                        {displayContact.phone && (
                          <ListItem>
                            <ListItemIcon>
                              <Phone />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Button
                                  href={`tel:${displayContact.phone}`}
                                  size="small"
                                  sx={{
                                    textTransform: 'none',
                                    p: 0,
                                    minWidth: 'auto',
                                  }}
                                >
                                  {displayContact.phone}
                                </Button>
                              }
                            />
                          </ListItem>
                        )}
                        {displayContact && (
                          <ListItem>
                            <ListItemText
                              primary={`${displayContact.first_name} ${displayContact.last_name}`}
                              secondary={
                                displayContact.is_primary
                                  ? 'Primary Contact'
                                  : 'Contact'
                              }
                            />
                          </ListItem>
                        )}
                      </List>
                    ) : (
                      <List dense>
                        {(bnb as any).phone && (
                          <ListItem>
                            <ListItemIcon>
                              <Phone />
                            </ListItemIcon>
                            <ListItemText primary={(bnb as any).phone} />
                          </ListItem>
                        )}
                        {(bnb as any).email && (
                          <ListItem>
                            <ListItemIcon>
                              <Email />
                            </ListItemIcon>
                            <ListItemText primary={(bnb as any).email} />
                          </ListItem>
                        )}
                        {(bnb as any).website && (
                          <ListItem>
                            <ListItemIcon>
                              <Language />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Button
                                  href={(bnb as any).website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  size="small"
                                >
                                  Visit Website
                                </Button>
                              }
                            />
                          </ListItem>
                        )}
                      </List>
                    )}
                    <Stack spacing={1} sx={{ mt: 2 }}>
                      {!hasAnyContactInfo && (
                        <Button
                          variant="contained"
                          startIcon={<Message />}
                          onClick={() => setShowContactModal(true)}
                          fullWidth
                        >
                          Send Message
                        </Button>
                      )}
                      {((bnb.pricing && bnb.pricing.length > 0) ||
                        (bnb.bookings && bnb.bookings.length > 0)) && (
                        <Button
                          variant="outlined"
                          startIcon={<CalendarToday />}
                          onClick={() => setShowAvailabilityModal(true)}
                          fullWidth
                        >
                          Availability / Pricing
                        </Button>
                      )}
                    </Stack>
                  </>
                );
              })()}
            </CardContent>
          </Card>

          {/* Check-in/Check-out Times */}
          {((bnb as any).check_in_time || (bnb as any).check_out_time) && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Check-in & Check-out
                </Typography>
                <List dense>
                  {(bnb as any).check_in_time && (
                    <ListItem>
                      <ListItemIcon>
                        <AccessTime />
                      </ListItemIcon>
                      <ListItemText
                        primary="Check-in"
                        secondary={(bnb as any).check_in_time}
                      />
                    </ListItem>
                  )}
                  {(bnb as any).check_out_time && (
                    <ListItem>
                      <ListItemIcon>
                        <AccessTime />
                      </ListItemIcon>
                      <ListItemText
                        primary="Check-out"
                        secondary={(bnb as any).check_out_time}
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          )}

          {/* Rating */}
          {(bnb as any).rating && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Guest Rating
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Star color="warning" />
                  <Typography variant="h6">{(bnb as any).rating}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    ({(bnb as any).review_count} reviews)
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Local Chapter */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Local Chapter
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Group color="action" />
                <Typography variant="body1">
                  {bnb.local_chapter_name}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Status */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Status
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Chip
                  label={bnb.is_active ? 'Active' : 'Inactive'}
                  color={bnb.is_active ? 'success' : 'error'}
                  size="small"
                />
                {(bnb as any).is_featured && (
                  <Chip label="Featured" color="primary" size="small" />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                Last updated: {dayjs(bnb.updated_at).format('MM/DD/YYYY')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Contact Message Modal */}
      <Dialog
        open={showContactModal}
        onClose={() => setShowContactModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Send Message to {bnb.name}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Send a message to the property contact with your desired dates. This
            message will be delivered via the server.
          </DialogContentText>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                autoFocus
                margin="dense"
                label="Check-in Date"
                type="date"
                fullWidth
                value={messageCheckInDate}
                onChange={(e) => setMessageCheckInDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Check-out Date"
                type="date"
                fullWidth
                value={messageCheckOutDate}
                onChange={(e) => setMessageCheckOutDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Your Message"
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Type your message here..."
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowContactModal(false)}>Cancel</Button>
          <Button
            onClick={handleSendMessage}
            variant="contained"
            disabled={
              !messageText.trim() ||
              !messageCheckInDate ||
              !messageCheckOutDate ||
              !dayjs(messageCheckInDate).isValid() ||
              !dayjs(messageCheckOutDate).isValid() ||
              isSendingMessage
            }
          >
            {isSendingMessage ? 'Sending...' : 'Send Message'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Modal */}
      <Dialog
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit {bnb.name}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Edit property contacts and annual fees. This functionality will be
            implemented in the next phase.
          </DialogContentText>
          <Typography variant="body2" color="text.secondary">
            Edit functionality coming soon...
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Availability Check Modal */}
      <Dialog
        open={showAvailabilityModal}
        onClose={() => setShowAvailabilityModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Check Availability</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select a date to check availability for {bnb.name}.
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                autoFocus
                margin="dense"
                label="Check-in Date"
                type="date"
                fullWidth
                value={availabilityStartDate}
                onChange={(e) => setAvailabilityStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Check-out Date"
                type="date"
                fullWidth
                value={availabilityEndDate}
                onChange={(e) => setAvailabilityEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          {availabilityResult && (
            <Box sx={{ mt: 2 }}>
              {availabilityResult.available ? (
                <Alert
                  severity="success"
                  sx={{ mt: 2 }}
                  onClose={() => setAvailabilityResult(null)}
                >
                  <AlertTitle>Available!</AlertTitle>
                  <Box>
                    <Typography variant="body1" gutterBottom>
                      {availabilityResult.message ||
                        'Available for the selected dates!'}
                    </Typography>
                    {availabilityResult.pricing &&
                      availabilityResult.pricing.length > 0 && (
                        <Box>
                          <Typography
                            variant="body2"
                            gutterBottom
                            sx={{ mt: 1, fontWeight: 'bold' }}
                          >
                            Pricing for your stay:
                          </Typography>
                          {availabilityResult.pricing.map((price, index) => (
                            <Typography
                              key={index}
                              variant="body2"
                              sx={{ ml: 2 }}
                            >
                              {price.name}: ${price.price}/night
                            </Typography>
                          ))}
                        </Box>
                      )}
                  </Box>
                </Alert>
              ) : (
                <AlertItem
                  severity="error"
                  title="Not Available"
                  description={
                    availabilityResult.message ||
                    'Not available for the selected dates.'
                  }
                  onClose={() => setAvailabilityResult(null)}
                  keepOpen={true}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAvailabilityModal(false)}>Close</Button>
          <Button
            onClick={handleCheckAvailability}
            variant="contained"
            disabled={
              !availabilityStartDate ||
              !availabilityEndDate ||
              !dayjs(availabilityStartDate).isValid() ||
              !dayjs(availabilityEndDate).isValid()
            }
          >
            Check Availability
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Booking Modal */}
      <Dialog
        open={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Booking</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Create a new booking for {bnb.name}. Enter the stay dates and
            booking details.
          </DialogContentText>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Check-in Date"
                type="date"
                fullWidth
                value={bookingForm.check_in_date}
                onChange={(e) =>
                  setBookingForm((prev) => ({
                    ...prev,
                    check_in_date: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Check-out Date"
                type="date"
                fullWidth
                value={bookingForm.check_out_date}
                onChange={(e) =>
                  setBookingForm((prev) => ({
                    ...prev,
                    check_out_date: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {selectedBookingUser ? (
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    bgcolor: 'green.50',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'green.200',
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography variant="subtitle2" color="primary">
                        Selected Guest:
                      </Typography>
                      <Typography variant="body2">
                        {selectedBookingUser.full_name}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setSelectedBookingUser(null);
                        setBookingForm((prev) => ({ ...prev, user_id: null }));
                      }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ mb: 2, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => setShowBookingUserSearchModal(true)}
                    sx={{ mb: 2 }}
                  >
                    Select Guest
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    Leave empty for blocked booking
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Booking Type</InputLabel>
                <Select
                  value={bookingForm.is_blocked ? 'blocked' : 'booking'}
                  onChange={(e) =>
                    setBookingForm((prev) => ({
                      ...prev,
                      is_blocked: e.target.value === 'blocked',
                    }))
                  }
                  label="Booking Type"
                >
                  <MenuItem value="booking">Actual Booking</MenuItem>
                  <MenuItem value="blocked">Blocked Dates</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={bookingForm.status}
                  onChange={(e) =>
                    setBookingForm((prev) => ({
                      ...prev,
                      status: e.target.value as
                        | 'confirmed'
                        | 'pending'
                        | 'cancelled',
                    }))
                  }
                  label="Status"
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={3}
                value={bookingForm.notes || ''}
                onChange={(e) =>
                  setBookingForm((prev) => ({
                    ...prev,
                    notes: e.target.value || null,
                  }))
                }
                placeholder="Additional notes about the booking..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowBookingModal(false);
              setSelectedBookingUser(null);
              setBookingForm({
                check_in_date: '',
                check_out_date: '',
                user_id: null,
                is_blocked: false,
                notes: '',
                status: 'pending',
              });
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateBooking}
            variant="contained"
            disabled={
              !bookingForm.check_in_date ||
              !bookingForm.check_out_date ||
              isCreatingBooking
            }
          >
            {isCreatingBooking ? 'Creating...' : 'Create Booking'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Booking Modal */}
      <Dialog
        open={showEditBookingModal}
        onClose={() => setShowEditBookingModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Booking</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Update the booking details for {bnb.name}.
          </DialogContentText>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Check-in Date"
                type="date"
                fullWidth
                value={bookingForm.check_in_date}
                onChange={(e) =>
                  setBookingForm((prev) => ({
                    ...prev,
                    check_in_date: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Check-out Date"
                type="date"
                fullWidth
                value={bookingForm.check_out_date}
                onChange={(e) =>
                  setBookingForm((prev) => ({
                    ...prev,
                    check_out_date: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="User ID"
                fullWidth
                value={bookingForm.user_id || ''}
                onChange={(e) =>
                  setBookingForm((prev) => ({
                    ...prev,
                    user_id: e.target.value || null,
                  }))
                }
                placeholder="Leave empty for blocked booking"
                helperText="Enter user ID for actual booking, leave empty for blocked dates"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Booking Type</InputLabel>
                <Select
                  value={bookingForm.is_blocked ? 'blocked' : 'booking'}
                  onChange={(e) =>
                    setBookingForm((prev) => ({
                      ...prev,
                      is_blocked: e.target.value === 'blocked',
                    }))
                  }
                  label="Booking Type"
                >
                  <MenuItem value="booking">Actual Booking</MenuItem>
                  <MenuItem value="blocked">Blocked Dates</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={bookingForm.status}
                  onChange={(e) =>
                    setBookingForm((prev) => ({
                      ...prev,
                      status: e.target.value as
                        | 'confirmed'
                        | 'pending'
                        | 'cancelled',
                    }))
                  }
                  label="Status"
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={3}
                value={bookingForm.notes || ''}
                onChange={(e) =>
                  setBookingForm((prev) => ({
                    ...prev,
                    notes: e.target.value || null,
                  }))
                }
                placeholder="Additional notes about the booking..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowEditBookingModal(false);
              setEditingBooking(null);
              setBookingForm({
                check_in_date: '',
                check_out_date: '',
                user_id: null,
                is_blocked: false,
                notes: '',
                status: 'pending',
              });
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateBooking}
            variant="contained"
            disabled={
              !bookingForm.check_in_date ||
              !bookingForm.check_out_date ||
              isUpdatingBooking
            }
          >
            {isUpdatingBooking ? 'Updating...' : 'Update Booking'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Pricing Modal */}
      <Dialog
        open={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Pricing</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Create a new pricing tier for {bnb.name}. Enter the pricing details
            and date range.
          </DialogContentText>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Pricing Name"
                fullWidth
                value={pricingForm.name}
                onChange={(e) =>
                  setPricingForm((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Price per Night"
                type="number"
                fullWidth
                value={pricingForm.price === 0 ? '' : pricingForm.price}
                onChange={(e) =>
                  setPricingForm((prev) => ({
                    ...prev,
                    price:
                      e.target.value === ''
                        ? 0
                        : parseFloat(e.target.value) || 0,
                  }))
                }
                inputProps={{
                  step: '0.01',
                  min: '0',
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                value={pricingForm.start_date}
                onChange={(e) =>
                  setPricingForm((prev) => ({
                    ...prev,
                    start_date: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="End Date"
                type="date"
                fullWidth
                value={pricingForm.end_date}
                onChange={(e) =>
                  setPricingForm((prev) => ({
                    ...prev,
                    end_date: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                helperText="Leave empty for ongoing pricing"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPricingModal(false)}>Cancel</Button>
          <Button
            onClick={handleCreatePricing}
            variant="contained"
            disabled={
              !pricingForm.name ||
              !pricingForm.price ||
              !pricingForm.start_date ||
              isCreatingPricing
            }
          >
            {isCreatingPricing ? 'Creating...' : 'Create Pricing'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Pricing Modal */}
      <Dialog
        open={showEditPricingModal}
        onClose={() => setShowEditPricingModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Pricing</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Update the pricing details for {bnb.name}.
          </DialogContentText>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Pricing Name"
                fullWidth
                value={pricingForm.name}
                onChange={(e) =>
                  setPricingForm((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Price per Night"
                type="number"
                fullWidth
                value={pricingForm.price === 0 ? '' : pricingForm.price}
                onChange={(e) =>
                  setPricingForm((prev) => ({
                    ...prev,
                    price:
                      e.target.value === ''
                        ? 0
                        : parseFloat(e.target.value) || 0,
                  }))
                }
                inputProps={{
                  step: '0.01',
                  min: '0',
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                value={pricingForm.start_date}
                onChange={(e) =>
                  setPricingForm((prev) => ({
                    ...prev,
                    start_date: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="End Date"
                type="date"
                fullWidth
                value={pricingForm.end_date}
                onChange={(e) =>
                  setPricingForm((prev) => ({
                    ...prev,
                    end_date: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                helperText="Leave empty for ongoing pricing"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditPricingModal(false)}>Cancel</Button>
          <Button
            onClick={handleUpdatePricing}
            variant="contained"
            disabled={
              !pricingForm.name ||
              !pricingForm.price ||
              !pricingForm.start_date ||
              isUpdatingPricing
            }
          >
            {isUpdatingPricing ? 'Updating...' : 'Update Pricing'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit BnB Modal */}
      <Dialog
        open={showEditBnBModal}
        onClose={() => setShowEditBnBModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Property Details</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Update the property information for {bnb.name}.
          </DialogContentText>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Property Name"
                fullWidth
                value={bnbForm.name || ''}
                onChange={(e) =>
                  setBnBForm((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={bnbForm.description || ''}
                onChange={(e) =>
                  setBnBForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Describe the property, its features, and what makes it special..."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CountrySelector
                handleChange={handleCountryChange}
                value={selectedCountry?.id}
                required={true}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StateSelector
                handleChange={handleStateChange}
                countryId={selectedCountry?.id}
                value={selectedState?.id}
                required={true}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="City"
                fullWidth
                value={bnbForm.city || ''}
                onChange={(e) =>
                  setBnBForm((prev) => ({
                    ...prev,
                    city: e.target.value,
                  }))
                }
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Amenities (comma-separated)"
                fullWidth
                value={amenitiesString}
                onChange={(e) => setAmenitiesString(e.target.value)}
                placeholder="WiFi, Pool, Parking, Kitchen, etc."
                helperText="Enter amenities separated by commas"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Policies"
                fullWidth
                multiline
                rows={3}
                value={bnbForm.policies || ''}
                onChange={(e) =>
                  setBnBForm((prev) => ({
                    ...prev,
                    policies: e.target.value,
                  }))
                }
                placeholder="General policies and rules for the property..."
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Cancellation Policy"
                fullWidth
                multiline
                rows={2}
                value={bnbForm.cancellation_policy || ''}
                onChange={(e) =>
                  setBnBForm((prev) => ({
                    ...prev,
                    cancellation_policy: e.target.value,
                  }))
                }
                placeholder="Cancellation terms and conditions..."
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Pet Policy"
                fullWidth
                multiline
                rows={2}
                value={bnbForm.pet_policy || ''}
                onChange={(e) =>
                  setBnBForm((prev) => ({
                    ...prev,
                    pet_policy: e.target.value,
                  }))
                }
                placeholder="Pet rules and restrictions..."
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Smoking Policy"
                fullWidth
                multiline
                rows={2}
                value={bnbForm.smoking_policy || ''}
                onChange={(e) =>
                  setBnBForm((prev) => ({
                    ...prev,
                    smoking_policy: e.target.value,
                  }))
                }
                placeholder="Smoking rules and restrictions..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Accessibility Features (comma-separated)"
                fullWidth
                value={accessibilityString}
                onChange={(e) => setAccessibilityString(e.target.value)}
                placeholder="Wheelchair accessible, Elevator, Ground floor, etc."
                helperText="Enter accessibility features separated by commas"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ChapterSelector
                chapter={selectedChapter?.id}
                handleChapterSelect={handleChapterChange}
                spd={null}
                keyId="bnb-detail"
                label="Local Chapter"
                objectReturned={true}
                all={true}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={bnbForm.is_active || false}
                    onChange={(e) =>
                      setBnBForm((prev) => ({
                        ...prev,
                        is_active: e.target.checked,
                      }))
                    }
                  />
                }
                label="Active Property"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowEditBnBModal(false);
              setBnBForm({});
              setSelectedCountry(null);
              setSelectedState(null);
              setSelectedChapter(null);
              setAmenitiesString('');
              setAccessibilityString('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateBnB}
            variant="contained"
            disabled={
              !bnbForm.name ||
              !bnbForm.city ||
              !selectedState?.id ||
              !selectedChapter?.id ||
              isUpdatingBnB
            }
          >
            {isUpdatingBnB ? 'Updating...' : 'Update Property'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Contact Modal */}
      <Dialog
        open={showAddContactModal}
        onClose={() => setShowAddContactModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Contact</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Add a new contact for {bnb.name}. Set if they should be the primary
            contact.
          </DialogContentText>
          {selectedUser && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography variant="subtitle2" color="primary">
                    Selected User:
                  </Typography>
                  <Typography variant="body2">
                    {selectedUser.full_name}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShowUserSearchModal(true)}
                >
                  Change User
                </Button>
              </Box>
            </Box>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={contactForm.is_primary || false}
                    onChange={(e) =>
                      setContactForm((prev) => ({
                        ...prev,
                        is_primary: e.target.checked,
                      }))
                    }
                  />
                }
                label="Primary Contact"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={contactForm.show_email || false}
                    onChange={(e) =>
                      setContactForm((prev) => ({
                        ...prev,
                        show_email: e.target.checked,
                        email: e.target.checked ? prev.email || '' : undefined,
                      }))
                    }
                  />
                }
                label="Show Email"
              />
            </Grid>
            {contactForm.show_email && (
              <Grid item xs={12}>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  value={contactForm.email || ''}
                  onChange={(e) =>
                    setContactForm((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  required={contactForm.show_email}
                  helperText={
                    contactForm.show_email && !contactForm.email
                      ? 'Email is required when "Show Email" is checked'
                      : ''
                  }
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={contactForm.show_phone || false}
                    onChange={(e) =>
                      setContactForm((prev) => ({
                        ...prev,
                        show_phone: e.target.checked,
                        phone: e.target.checked ? prev.phone || '' : undefined,
                      }))
                    }
                  />
                }
                label="Show Phone"
              />
            </Grid>
            {contactForm.show_phone && (
              <Grid item xs={12}>
                <TextField
                  label="Phone"
                  type="tel"
                  fullWidth
                  value={contactForm.phone || ''}
                  onChange={(e) =>
                    setContactForm((prev) => ({
                      ...prev,
                      phone: e.target.value,
                    }))
                  }
                  required={contactForm.show_phone}
                  helperText={
                    contactForm.show_phone && !contactForm.phone
                      ? 'Phone is required when "Show Phone" is checked'
                      : ''
                  }
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowAddContactModal(false);
              setSelectedUser(null);
              setContactForm({
                user_id: '',
                is_primary: false,
                show_email: false,
                email: '',
                show_phone: false,
                phone: '',
              });
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateContact}
            variant="contained"
            disabled={
              !contactForm.user_id ||
              isCreatingContact ||
              (contactForm.show_email && !contactForm.email) ||
              (contactForm.show_phone && !contactForm.phone)
            }
          >
            {isCreatingContact ? 'Adding...' : 'Add Contact'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Contact Modal */}
      <Dialog
        open={showEditContactModal}
        onClose={() => setShowEditContactModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Contact</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Update the contact details for {bnb.name}.
          </DialogContentText>

          {/* Current Contact Info */}
          {editingContact && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                bgcolor: 'blue.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'blue.200',
              }}
            >
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                Current Contact:
              </Typography>
              <Typography variant="body2">
                {editingContact.first_name} {editingContact.last_name}
              </Typography>
            </Box>
          )}

          {/* Selected New User Info */}
          {selectedUser && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                bgcolor: 'green.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'green.200',
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography variant="subtitle2" color="primary">
                    New User Selected:
                  </Typography>
                  <Typography variant="body2">
                    {selectedUser.full_name}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setSelectedUser(null);
                    setContactForm((prev) => ({ ...prev, user_id: '' }));
                  }}
                >
                  Clear Selection
                </Button>
              </Box>
            </Box>
          )}

          {/* User Selection Button */}
          {!selectedUser && (
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => setShowUserSearchModal(true)}
                sx={{ mb: 2 }}
              >
                Select New User
              </Button>
              <Typography variant="body2" color="text.secondary">
                Keep current user or select a new one
              </Typography>
            </Box>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={contactForm.is_primary || false}
                    onChange={(e) =>
                      setContactForm((prev) => ({
                        ...prev,
                        is_primary: e.target.checked,
                      }))
                    }
                  />
                }
                label="Primary Contact"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={contactForm.show_email || false}
                    onChange={(e) =>
                      setContactForm((prev) => ({
                        ...prev,
                        show_email: e.target.checked,
                        email: e.target.checked ? prev.email || '' : undefined,
                      }))
                    }
                  />
                }
                label="Show Email"
              />
            </Grid>
            {contactForm.show_email && (
              <Grid item xs={12}>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  value={contactForm.email || ''}
                  onChange={(e) =>
                    setContactForm((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  required={contactForm.show_email}
                  helperText={
                    contactForm.show_email && !contactForm.email
                      ? 'Email is required when "Show Email" is checked'
                      : ''
                  }
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={contactForm.show_phone || false}
                    onChange={(e) =>
                      setContactForm((prev) => ({
                        ...prev,
                        show_phone: e.target.checked,
                        phone: e.target.checked ? prev.phone || '' : undefined,
                      }))
                    }
                  />
                }
                label="Show Phone"
              />
            </Grid>
            {contactForm.show_phone && (
              <Grid item xs={12}>
                <TextField
                  label="Phone"
                  type="tel"
                  fullWidth
                  value={contactForm.phone || ''}
                  onChange={(e) =>
                    setContactForm((prev) => ({
                      ...prev,
                      phone: e.target.value,
                    }))
                  }
                  required={contactForm.show_phone}
                  helperText={
                    contactForm.show_phone && !contactForm.phone
                      ? 'Phone is required when "Show Phone" is checked'
                      : ''
                  }
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowEditContactModal(false);
              setSelectedUser(null);
              setEditingContact(null);
              setContactForm({
                user_id: '',
                is_primary: false,
                show_email: false,
                email: '',
                show_phone: false,
                phone: '',
              });
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateContact}
            variant="contained"
            disabled={isUpdatingContact}
          >
            {isUpdatingContact ? 'Updating...' : 'Update Contact'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Search Modal */}
      <UserSearchModal
        modalOpen={showUserSearchModal}
        setUser={handleUserSelected}
        setModalOpen={setShowUserSearchModal}
        chapter={null}
        spd={null}
        userObjectRet={true}
        allowSetToMe={true}
        statusFilter="active"
      />

      {/* User Search Modal for Booking Guest Selection */}
      <UserSearchModal
        modalOpen={showBookingUserSearchModal}
        setUser={handleBookingUserSelected}
        setModalOpen={setShowBookingUserSearchModal}
        chapter={null}
        spd={null}
        userObjectRet={true}
        allowSetToMe={true}
        statusFilter="active"
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={showDeleteConfirm}
        handleCancel={handleCancelDelete}
        handleOk={handleConfirmDelete}
        title="Delete Contact"
        message="Are you sure you want to delete this contact? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Booking Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={showBookingDeleteConfirm}
        handleCancel={handleCancelBookingDelete}
        handleOk={handleConfirmBookingDelete}
        title="Delete Booking"
        message="Are you sure you want to delete this booking? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Pricing Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={showPricingDeleteConfirm}
        handleCancel={handleCancelPricingDelete}
        handleOk={handleConfirmPricingDelete}
        title="Delete Pricing"
        message="Are you sure you want to delete this pricing tier? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </Box>
  );
};

export default BnBDetail;
